/*
 * How a file gets into the tray: dropped, pasted or picked.
 *
 * The window guard is the part that is easy to leave out and expensive to miss. A file dropped
 * anywhere the application does not handle makes the browser *navigate to it* — the tab leaves the
 * app, and the draft, the socket and everything unsent go with it. So the guard is window-level and
 * in the capture phase, it counts enter and leave (a drag over a child fires `dragleave` on the
 * parent), and it refuses the drop even where nothing wants the file.
 *
 * A paste is the other half. A screenshot arrives as an image item, and a copy from a document
 * arrives as an image *and* its text; blindly calling `preventDefault()` on any paste that carries
 * a file loses the text. This file only ever reports what a paste contains and lets the composer
 * decide.
 */

import { useEffect, useRef, useSyncExternalStore } from "react"

/** The parts of a `DataTransfer` this file reads; a test hands in an object of the same shape. */
export interface TransferLike {
	files?: { length: number; item(index: number): File | null } | FileList
	types?: readonly string[]
}

function fileList(transfer: TransferLike): File[] {
	const files = transfer.files
	if (files === undefined) return []
	const out: File[] = []
	for (let i = 0; i < files.length; i++) {
		const file = files.item(i)
		if (file !== null) out.push(file)
	}
	return out
}

/** Whether a drag carries files at all — a dragged selection of text must not open the overlay. */
export function carriesFiles(transfer: TransferLike): boolean {
	return (transfer.types ?? []).includes("Files")
}

/**
 * The files of a drop.
 *
 * A folder dropped from the desktop arrives as a zero-byte entry with no type, and uploading it
 * would store an empty file under the folder's name; it is skipped here rather than refused at the
 * server, because there is nothing the person could do about it.
 */
export function droppedFiles(transfer: TransferLike): File[] {
	return fileList(transfer).filter((file) => file.size > 0 || file.type !== "")
}

/** What a paste carries: its files, and whether there is text that must still be inserted. */
export interface PastedContent {
	files: File[]
	hasText: boolean
}

export function pastedContent(transfer: TransferLike): PastedContent {
	const types = transfer.types ?? []
	return {
		files: droppedFiles(transfer),
		hasText: types.includes("text/plain") || types.includes("text/html"),
	}
}

/**
 * A screenshot pasted from the clipboard has no name — every browser calls it `image.png`, and ten
 * of them in one conversation are indistinguishable. It is named after the moment it was pasted,
 * which is both unique and something a person can recognise later; the chip offers Rename.
 */
export function namePastedImage(file: File, at = new Date()): File {
	if (file.name !== "" && file.name !== "image.png") return file
	const stamp = [
		at.getFullYear(),
		String(at.getMonth() + 1).padStart(2, "0"),
		String(at.getDate()).padStart(2, "0"),
		String(at.getHours()).padStart(2, "0"),
		String(at.getMinutes()).padStart(2, "0"),
		String(at.getSeconds()).padStart(2, "0"),
	].join("")
	const extension = file.type.split("/")[1] ?? "png"
	return new File([file], `pasted-${stamp}.${extension}`, { type: file.type })
}

// ---- the window guard ----

type DropHandler = (files: File[]) => void

/**
 * Who takes a file dropped anywhere on the page. It is a stack rather than a single slot: a thread
 * pane or an in-place edit box opens over a conversation, and while it is open its composer is the
 * one a drop belongs to.
 */
const windowTargets: DropHandler[] = []

/**
 * What every surface that draws a "drop it here" needs to know: whether a drag carrying files is
 * over the window, and how many composers are registered for it. The count is the whole reason
 * this is shared state rather than each hook's own — the last composer registered is the one that
 * will get the file, so a surface that is not it must not offer to take it.
 */
export interface FileDrag {
	dragging: boolean
	composers: number
}

let drag: FileDrag = { dragging: false, composers: 0 }
const dragListeners = new Set<() => void>()

/**
 * Surfaces that draw a drop overlay of their own over the composer they contain — the conversation
 * pane. One drag is one offer: while such a surface is on screen and its composer is the only one,
 * the box inside it draws no second "drop it here" of its own.
 */
let panes = 0

/** Enters minus leaves: a drag moving from an element to its child leaves the parent first. */
let dragDepth = 0

/** True while a pane is answering for the composer that would take the file. */
function paneAnswers(): boolean {
	return panes > 0 && windowTargets.length === 1
}

function publish(dragging: boolean): void {
	const composers = windowTargets.length
	// A new object per change and the same one in between: `useSyncExternalStore` compares
	// snapshots by identity and re-renders for ever when a getter builds a fresh one per call.
	if (dragging !== drag.dragging || composers !== drag.composers) drag = { dragging, composers }
	// Every listener is told even when the snapshot is unchanged: a composer's own answer also
	// depends on where it sits in the stack and on whether a pane is answering for it, and neither
	// of those is in the snapshot.
	for (const listener of dragListeners) listener()
}

function onDragOver(event: DragEvent): void {
	event.preventDefault()
}

function onDragEnter(event: DragEvent): void {
	if (event.dataTransfer === null || !carriesFiles(event.dataTransfer)) return
	dragDepth += 1
	publish(true)
}

function onDragLeave(): void {
	dragDepth = Math.max(0, dragDepth - 1)
	if (dragDepth === 0) publish(false)
}

function onDrop(event: DragEvent): void {
	event.preventDefault()
	dragDepth = 0
	publish(false)
	const files = event.dataTransfer === null ? [] : droppedFiles(event.dataTransfer)
	const target = windowTargets[windowTargets.length - 1]
	if (files.length > 0 && target !== undefined) target(files)
}

let guards = 0

/**
 * The four window listeners, installed while anything is interested in a drag and removed with the
 * last of them. `dragover` and `drop` are in the capture phase because the refusal has to happen
 * before any element sees the event; the two counting listeners are ordinary.
 */
function retainGuard(): () => void {
	guards += 1
	if (guards === 1) {
		window.addEventListener("dragover", onDragOver, true)
		window.addEventListener("drop", onDrop, true)
		window.addEventListener("dragenter", onDragEnter)
		window.addEventListener("dragleave", onDragLeave)
	}
	return () => {
		guards -= 1
		if (guards > 0) return
		window.removeEventListener("dragover", onDragOver, true)
		window.removeEventListener("drop", onDrop, true)
		window.removeEventListener("dragenter", onDragEnter)
		window.removeEventListener("dragleave", onDragLeave)
		dragDepth = 0
		publish(false)
	}
}

function subscribeDrag(listener: () => void): () => void {
	dragListeners.add(listener)
	return () => {
		dragListeners.delete(listener)
	}
}

/**
 * The window's drag, for a surface that draws an overlay but owns no composer — the conversation,
 * whose whole pane is a drop target for the box below it (UX §8 #14). It takes no drop of its own:
 * the guard is retained so that the counting listeners run, and the file still lands in the
 * composer the stack names.
 *
 * @param drawsOverlay this surface answers for the composer it contains, so that composer draws no
 * overlay of its own. It is declared for the surface's whole life and not only while a drag is on:
 * the two decisions then read the same three numbers and can never disagree for a frame.
 */
export function useFileDrag(drawsOverlay = false): FileDrag {
	useEffect(() => {
		const release = retainGuard()
		if (!drawsOverlay) return release
		panes += 1
		publish(drag.dragging)
		return () => {
			panes -= 1
			publish(drag.dragging)
			release()
		}
	}, [drawsOverlay])
	return useSyncExternalStore(subscribeDrag, () => drag)
}

/**
 * Makes this composer the one a file dropped anywhere lands in, and keeps the browser from
 * navigating to it. The answer — draw your own "drop it here" — is true while a drag carrying files
 * is over the window, this composer is the one on top of the stack, and no pane is answering for
 * it. A box must not offer to take a file a thread pane's box would get, and it must not repeat an
 * offer the pane around it is already making.
 */
export function useWindowFileDrop(onFiles: DropHandler): boolean {
	// The registration must not be torn down and rebuilt on every render, so the handler is reached
	// through a box the effect reads at drop time rather than captured when it registers — and the
	// box itself is this instance's identity in the stack.
	const handler = useRef(onFiles)
	useEffect(() => {
		handler.current = onFiles
	}, [onFiles])
	const take = useRef<DropHandler | null>(null)
	take.current ??= (files) => handler.current(files)

	useEffect(() => {
		const mine = take.current
		if (mine === null) return
		windowTargets.push(mine)
		publish(drag.dragging)
		const release = retainGuard()
		return () => {
			const at = windowTargets.indexOf(mine)
			if (at >= 0) windowTargets.splice(at, 1)
			publish(drag.dragging)
			release()
		}
	}, [])

	return useSyncExternalStore(
		subscribeDrag,
		() =>
			drag.dragging && !paneAnswers() && windowTargets[windowTargets.length - 1] === take.current,
	)
}

/**
 * There is deliberately no second, element-level drop handler for a composer box or for the pane
 * around it. The guard above runs in the capture phase, so it sees every drop before any element
 * does; a box that handled its own drop as well would stage the same file twice. Every surface
 * draws its overlay from the state above and lets the window handler do the work.
 */
